"use client";

import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";

import SingleMonthPnlPage from "./pnl/page";
import PnlCumulativePage from "./pnl-cumulative/page";
import TrialBalancePage from "./trial-balance/page";
import BalanceSheetPage from "./balance-sheet/page";

export default function Dashboard() {
  const [globalFile, setGlobalFile] = useState(null);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [globalSheet, setGlobalSheet] = useState("");
  const [activeTab, setActiveTab] = useState("pnl-cum");

  // Password Modal State
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isDecrypting, setIsDecrypting] = useState(false);

  const fileInputRef = useRef(null);

  // Fast sheet-name reader (skips heavy cell parsing)
  const extractSheetNames = (buffer, fileObj) => {
    try {
      const workbook = XLSX.read(buffer, { type: "array", bookSheets: true });
      const sheets = workbook.SheetNames || [];
      setAvailableSheets(sheets);

      const detectedTbSheet =
        sheets.find((s) => /trial\s*balance|tb/i.test(s)) || sheets[0] || "";
      setGlobalSheet(detectedTbSheet);
      setGlobalFile(fileObj);
    } catch (err) {
      console.error("Error reading sheets:", err);
    }
  };

  const handleGlobalFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPasswordError("");
    setPassword("");

    try {
      const buffer = await file.arrayBuffer();

      // Check if file is password protected by attempting a fast metadata read
      try {
        const workbook = XLSX.read(buffer, { type: "array", bookSheets: true });
        // Succeeded -> Not password protected
        extractSheetNames(buffer, file);
      } catch (err) {
        // Failed -> Password protected file
        setPendingFile(file);
        setIsPasswordModalOpen(true);
      }
    } catch (err) {
      console.error("File read error:", err);
    }
  };

  const handleUnlockPassword = async (e) => {
    e?.preventDefault();
    const rawFile = pendingFile;
    if (!password.trim() || !rawFile) return;

    setIsDecrypting(true);
    setPasswordError("");

    try {
      // Decrypt on the backend using native C++ OpenSSL (~20ms)
      const formData = new FormData();
      formData.append("file", rawFile);
      formData.append("password", password);

      const response = await fetch("/api/decrypt-excel", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Incorrect password.");
      }

      const decryptedBlob = await response.blob();
      const decryptedBuffer = await decryptedBlob.arrayBuffer();

      // Create an unencrypted File object so all tabs receive clean data
      const cleanFile = new File([decryptedBuffer], rawFile.name, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        lastModified: Date.now(),
      });

      extractSheetNames(decryptedBuffer, cleanFile);

      // Close modal
      setIsPasswordModalOpen(false);
      setPendingFile(null);
      setPassword("");
    } catch (err) {
      console.error("Unlock error:", err);
      setPasswordError(err.message || "Incorrect password.");
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleCancelPasswordModal = () => {
    setIsPasswordModalOpen(false);
    setPendingFile(null);
    setPassword("");
    setPasswordError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex h-screen bg-slate-900 text-white font-sans overflow-hidden">
      {/* Sidebar Navigation */}
      <div className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col flex-shrink-0 z-10 shadow-lg">
        <div className="p-5 border-b border-slate-700">
          <h1 className="text-lg font-bold text-sky-400 tracking-wide">
            FINANCE PORTAL
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Universal extraction engine
          </p>
        </div>

        {/* Global File & Sheet Selection */}
        <div className="p-4 border-b border-slate-700 bg-slate-800/50 space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-2 font-semibold uppercase tracking-wider">
              Workspace File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleGlobalFileChange}
              className="text-xs w-full text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-bold file:bg-sky-600 file:text-white hover:file:bg-sky-500 cursor-pointer"
            />
            {globalFile && (
              <p className="text-xs text-emerald-400 mt-2 truncate font-medium">
                ✓ Loaded: {globalFile.name}
              </p>
            )}
          </div>

          {/* Global Sheet Selector */}
          {availableSheets.length > 0 && (
            <div>
              <label className="block text-xs text-slate-400 mb-1 font-semibold uppercase tracking-wider">
                Default Sheet ({availableSheets.length} found)
              </label>
              <select
                value={globalSheet}
                onChange={(e) => setGlobalSheet(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 text-xs text-sky-400 rounded px-2 py-1.5 focus:outline-none focus:border-sky-500 cursor-pointer"
              >
                {availableSheets.map((sh, i) => (
                  <option key={i} value={sh}>
                    {sh}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <TabButton
            active={activeTab === "pnl-cum"}
            onClick={() => setActiveTab("pnl-cum")}
          >
            Profit & Loss (Cumulative Multi-Period)
          </TabButton>
          <TabButton
            active={activeTab === "pnl-single"}
            onClick={() => setActiveTab("pnl-single")}
          >
            Profit & Loss (Single/Analysis)
          </TabButton>
          <div className="my-2 border-b border-slate-700"></div>
          <TabButton
            active={activeTab === "bs-cum"}
            onClick={() => setActiveTab("bs-cum")}
          >
            Balance Sheet (Cumulative Multi-Period)
          </TabButton>
          <div className="my-2 border-b border-slate-700"></div>
          <TabButton
            active={activeTab === "tb-import"}
            onClick={() => setActiveTab("tb-import")}
          >
            Check Trial Balance
          </TabButton>
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 relative overflow-auto bg-slate-900">
        <div
          style={{
            display: activeTab === "pnl-cum" ? "block" : "none",
            minHeight: "100%",
          }}
        >
          <PnlCumulativePage
            globalFile={globalFile}
            globalSheet={globalSheet}
            availableSheets={availableSheets}
          />
        </div>

        <div
          style={{
            display: activeTab === "pnl-single" ? "block" : "none",
            minHeight: "100%",
          }}
        >
          <SingleMonthPnlPage
            globalFile={globalFile}
            globalSheet={globalSheet}
            availableSheets={availableSheets}
          />
        </div>

        <div
          style={{
            display: activeTab === "bs-cum" ? "block" : "none",
            minHeight: "100%",
          }}
        >
          <BalanceSheetPage
            globalFile={globalFile}
            globalSheet={globalSheet}
            availableSheets={availableSheets}
          />
        </div>

        <div
          style={{
            display: activeTab === "tb-import" ? "block" : "none",
            minHeight: "100%",
          }}
        >
          <TrialBalancePage
            globalFile={globalFile}
            globalSheet={globalSheet}
            availableSheets={availableSheets}
          />
        </div>
      </div>

      {/* Password Modal Popup */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-500/10 rounded-lg text-amber-400">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-white">
                  Password Protected File
                </h3>
                <p className="text-xs text-slate-400 truncate max-w-[280px]">
                  {pendingFile?.name}
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              This spreadsheet is password-protected. Enter the password to
              unlock it for your workspace.
            </p>

            <form onSubmit={handleUnlockPassword} className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoFocus
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError("");
                  }}
                  placeholder="Enter file password..."
                  className="w-full bg-slate-900 border border-slate-700 text-sm text-white rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 text-xs px-1"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              {passwordError && (
                <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 p-2.5 rounded-lg flex items-center gap-2">
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{passwordError}</span>
                </div>
              )}

              <div className="flex items-center justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={handleCancelPasswordModal}
                  disabled={isDecrypting}
                  className="px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isDecrypting || !password.trim()}
                  className="px-4 py-2 text-xs font-semibold text-white bg-sky-600 hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors flex items-center gap-2"
                >
                  {isDecrypting ? (
                    <>
                      <svg
                        className="animate-spin h-3.5 w-3.5 text-white"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                      <span>Unlocking...</span>
                    </>
                  ) : (
                    <span>Unlock File</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
        active
          ? "bg-sky-500/10 text-sky-400 border border-sky-500/20 shadow-sm"
          : "text-slate-300 hover:bg-slate-700/50 hover:text-white border border-transparent"
      }`}
    >
      {children}
    </button>
  );
}